import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  CheckCircle2,
  Loader2,
  Mail,
  Save,
  ShieldCheck,
  X,
} from 'lucide-react';

import { rbacSettingsApi } from '../../settings/services/rbacSettingsApi';
import { staffApi } from '../services/staffApi';
import { getStaffApiErrorMessage } from '../utils/staffErrors';

const normalizeRoleId = (role) =>
  String(role?.id || role?.role_id || role || '');

const StaffAccountAccessModal = ({
  open,
  schoolId,
  staff,
  onClose,
  onSaved,
}) => {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [resending, setResending] = useState(false);
  const [roles, setRoles] = useState([]);
  const [detail, setDetail] = useState(null);
  const [loginEnabled, setLoginEnabled] = useState(false);
  const [selectedRoleIds, setSelectedRoleIds] = useState([]);
  const [message, setMessage] = useState(null);

  const staffId = staff?.id;

  const selectedRoleCount = selectedRoleIds.length;
  const isVerified = Boolean(detail?.user_is_verified);

  const loadAccessDetails = useCallback(async () => {
    if (!open || !schoolId || !staffId) return;

    setLoading(true);
    setMessage(null);

    try {
      const [staffResponse, roleList] = await Promise.all([
        staffApi.getStaffById(schoolId, staffId),
        rbacSettingsApi.listRoles(schoolId),
      ]);

      const staffDetail = staffResponse?.data ?? staffResponse;
      const activeRoles = (roleList || []).filter(
        (role) => !role.status || role.status === 'ACTIVE',
      );

      let assignedRoleIds = [];
      if (staffDetail?.user_id) {
        const assignedRoles = await rbacSettingsApi.listUserRoles(
          schoolId,
          staffDetail.user_id,
        );

        assignedRoleIds = (assignedRoles || [])
          .map(normalizeRoleId)
          .filter(Boolean);
      }

      setDetail(staffDetail);
      setRoles(activeRoles);
      setSelectedRoleIds([...new Set(assignedRoleIds)]);
      setLoginEnabled(
        staffDetail?.user_is_active === true ||
          staffDetail?.login_status === 'ENABLED',
      );
    } catch (error) {
      setMessage({
        type: 'error',
        text: getStaffApiErrorMessage(
          error,
          'Unable to load login access details.',
        ),
      });
    } finally {
      setLoading(false);
    }
  }, [open, schoolId, staffId]);

  useEffect(() => {
    loadAccessDetails();
  }, [loadAccessDetails]);

  useEffect(() => {
    if (!open) {
      setDetail(null);
      setRoles([]);
      setSelectedRoleIds([]);
      setMessage(null);
      setSaving(false);
      setResending(false);
    }
  }, [open]);

  const currentLoginEnabled = useMemo(
    () =>
      detail?.user_is_active === true ||
      detail?.login_status === 'ENABLED',
    [detail],
  );

  const toggleRole = (roleId) => {
    const normalized = String(roleId);

    setSelectedRoleIds((current) =>
      current.includes(normalized)
        ? current.filter((id) => id !== normalized)
        : [...current, normalized],
    );

    setMessage(null);
  };

  const synchronizeRoles = async (userId) => {
    if (!userId) {
      throw new Error('The linked user ID was not returned.');
    }

    const assignedRoles = await rbacSettingsApi.listUserRoles(
      schoolId,
      userId,
    );

    const currentRoleIds = new Set(
      (assignedRoles || []).map(normalizeRoleId).filter(Boolean),
    );
    const desiredRoleIds = new Set(selectedRoleIds.map(String));

    const rolesToAdd = [...desiredRoleIds].filter(
      (roleId) => !currentRoleIds.has(roleId),
    );
    const rolesToRemove = [...currentRoleIds].filter(
      (roleId) => !desiredRoleIds.has(roleId),
    );

    for (const roleId of rolesToAdd) {
      await rbacSettingsApi.assignUserRole(schoolId, userId, roleId);
    }

    for (const roleId of rolesToRemove) {
      await rbacSettingsApi.removeUserRole(schoolId, userId, roleId);
    }
  };

  const handleSave = async () => {
    if (saving || !schoolId || !staffId) return;

    if (loginEnabled && selectedRoleIds.length === 0) {
      setMessage({
        type: 'error',
        text: 'Select at least one role before enabling login access.',
      });
      return;
    }

    setSaving(true);
    setMessage(null);

    try {
      let userId = detail?.user_id || null;

      if (
        loginEnabled !== currentLoginEnabled ||
        (loginEnabled && !userId)
      ) {
        const toggleResponse = await staffApi.toggleLoginAccess(
          schoolId,
          staffId,
          loginEnabled,
          {
            roleIds: loginEnabled ? selectedRoleIds : undefined,
            sendActivationEmail: true,
          },
        );

        const toggledStaff = toggleResponse?.data ?? toggleResponse;
        userId = toggledStaff?.user_id || userId;
      }

      if (loginEnabled) {
        if (!userId) {
          const refreshedResponse = await staffApi.getStaffById(
            schoolId,
            staffId,
          );
          const refreshedStaff = refreshedResponse?.data ?? refreshedResponse;
          userId = refreshedStaff?.user_id || null;
        }

        await synchronizeRoles(userId);
      }

      const successText = loginEnabled
        ? 'Login access and roles saved successfully.'
        : 'Login access disabled successfully.';

      if (onSaved) {
        await onSaved(successText);
      }

      onClose();
    } catch (error) {
      setMessage({
        type: 'error',
        text: getStaffApiErrorMessage(
          error,
          'Unable to save login access.',
        ),
      });
    } finally {
      setSaving(false);
    }
  };

  const handleResendActivation = async () => {
    if (resending || isVerified) return;

    setResending(true);
    setMessage(null);

    try {
      const response = await staffApi.resendActivationEmail(
        schoolId,
        staffId,
      );

      setMessage({
        type: 'success',
        text:
          response?.message ||
          'Activation email sent successfully.',
      });
    } catch (error) {
      setMessage({
        type: 'error',
        text: getStaffApiErrorMessage(
          error,
          'Unable to resend the activation email.',
        ),
      });
    } finally {
      setResending(false);
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/45 p-4">
      <div className="flex max-h-[88vh] w-full max-w-3xl flex-col overflow-hidden rounded-2xl bg-white shadow-2xl">
        <div className="flex items-start justify-between border-b border-gray-200 px-6 py-5">
          <div>
            <h2 className="text-xl font-semibold text-gray-900">
              Manage Login Access
            </h2>
            <p className="mt-1 text-sm text-gray-500">
              {detail?.full_name || staff?.full_name || 'Staff member'}
            </p>
          </div>

          <button
            type="button"
            onClick={onClose}
            disabled={saving}
            className="rounded-lg p-2 text-gray-400 transition hover:bg-gray-100 hover:text-gray-700 disabled:cursor-not-allowed disabled:opacity-50"
            aria-label="Close login access"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto px-6 py-5">
          {loading ? (
            <div className="flex min-h-72 flex-col items-center justify-center">
              <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
              <p className="mt-3 text-sm text-gray-500">
                Loading login access...
              </p>
            </div>
          ) : (
            <div className="space-y-5">
              {message && (
                <div
                  className={`flex items-start gap-3 rounded-xl border p-4 text-sm ${
                    message.type === 'success'
                      ? 'border-green-200 bg-green-50 text-green-700'
                      : 'border-red-200 bg-red-50 text-red-700'
                  }`}
                >
                  <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0" />
                  <p>{message.text}</p>
                </div>
              )}

              <label className="flex cursor-pointer items-start justify-between gap-4 rounded-xl border border-gray-200 p-5">
                <div>
                  <p className="font-semibold text-gray-900">
                    Staff portal login
                  </p>
                  <p className="mt-1 text-sm leading-6 text-gray-500">
                    Enable or disable access to the staff portal for this account.
                  </p>
                  <p className="mt-2 text-sm text-gray-600">
                    {detail?.email || staff?.email || 'No email available'}
                  </p>
                </div>

                <input
                  type="checkbox"
                  checked={loginEnabled}
                  onChange={(event) => {
                    setLoginEnabled(event.target.checked);
                    setMessage(null);
                  }}
                  className="mt-1 h-5 w-5 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
              </label>

              {loginEnabled && (
                <section className="rounded-xl border border-gray-200 p-5">
                  <div className="flex items-start gap-3">
                    <ShieldCheck className="mt-0.5 h-5 w-5 text-blue-600" />
                    <div>
                      <h3 className="font-semibold text-gray-900">
                        Assigned roles
                      </h3>
                      <p className="mt-1 text-sm text-gray-500">
                        {selectedRoleCount} role{selectedRoleCount === 1 ? '' : 's'} selected
                      </p>
                    </div>
                  </div>

                  <div className="mt-5 grid grid-cols-1 gap-3 sm:grid-cols-2">
                    {roles.map((role) => {
                      const roleId = normalizeRoleId(role);
                      const checked = selectedRoleIds.includes(roleId);

                      return (
                        <label
                          key={roleId}
                          className={`flex cursor-pointer items-start gap-3 rounded-xl border p-4 transition ${
                            checked
                              ? 'border-blue-300 bg-blue-50'
                              : 'border-gray-200 hover:bg-gray-50'
                          }`}
                        >
                          <input
                            type="checkbox"
                            checked={checked}
                            onChange={() => toggleRole(roleId)}
                            className="mt-1 h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                          />

                          <div>
                            <p className="text-sm font-semibold text-gray-900">
                              {role.name}
                            </p>
                            <p className="mt-1 text-xs leading-5 text-gray-500">
                              {role.description || role.slug || 'School role'}
                            </p>
                          </div>
                        </label>
                      );
                    })}
                  </div>
                </section>
              )}

              {detail?.user_id && (
                <section className="flex flex-col gap-4 rounded-xl border border-gray-200 p-5 sm:flex-row sm:items-center sm:justify-between">
                  <div className="flex items-start gap-3">
                    <Mail className="mt-0.5 h-5 w-5 text-gray-500" />
                    <div>
                      <h3 className="font-semibold text-gray-900">
                        Activation email
                      </h3>
                      <p className="mt-1 text-sm leading-6 text-gray-500">
                        {isVerified
                          ? 'This user has already verified the account.'
                          : 'Resend the account activation link when the user has not created a password.'}
                      </p>
                    </div>
                  </div>

                  <button
                    type="button"
                    onClick={handleResendActivation}
                    disabled={resending || isVerified || saving}
                    className="inline-flex items-center justify-center gap-2 rounded-lg border border-gray-300 px-4 py-2.5 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    {resending && <Loader2 className="h-4 w-4 animate-spin" />}
                    {isVerified ? 'Already verified' : 'Resend activation'}
                  </button>
                </section>
              )}
            </div>
          )}
        </div>

        <div className="flex items-center justify-end gap-3 border-t border-gray-200 bg-white px-6 py-4">
          <button
            type="button"
            onClick={onClose}
            disabled={saving}
            className="rounded-lg border border-gray-300 px-5 py-2.5 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
          >
            Close
          </button>

          <button
            type="button"
            onClick={handleSave}
            disabled={loading || saving}
            className="inline-flex items-center justify-center gap-2 rounded-lg bg-blue-600 px-5 py-2.5 text-sm font-medium text-white hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-60"
          >
            {saving ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Save className="h-4 w-4" />
            )}
            {saving ? 'Saving...' : 'Save access'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default StaffAccountAccessModal;
